import { ConfirmProvider, ToastProvider } from '@coach/ui';
import { lazy, Suspense } from 'react';
import { HashRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { APP } from './app.config';
import { AuthGate } from './lib/auth';
import { PolicyNotice } from './lib/legal';
import { AppMark } from './lib/brand';
import { useApp } from './lib/app';
import { SyncBadge } from './features/shell/SyncBadge';
import { LiveSessionProvider, LiveBadge } from './features/plan/liveSession';
import { PlanView } from './features/plan/PlanView';

const DrillsView = lazy(() => import('./features/drills/DrillsView').then((m) => ({ default: m.DrillsView })));
const PlaybookView = lazy(() => import('./features/playbook/PlaybookView').then((m) => ({ default: m.PlaybookView })));
const AttendanceView = lazy(() => import('./features/attendance/AttendanceView').then((m) => ({ default: m.AttendanceView })));
const LineupView = lazy(() => import('./features/lineup/LineupView').then((m) => ({ default: m.LineupView })));
const EvalView = lazy(() => import('./features/eval/EvalView').then((m) => ({ default: m.EvalView })));
const SettingsView = lazy(() => import('./features/settings/SettingsView').then((m) => ({ default: m.SettingsView })));

const NAV: { to: string; icon: string; label: string; head?: boolean }[] = [
  // The first five are the phone tab bar; the rest live under More (Settings).
  { to: '/plan', icon: '📋', label: 'Plan' },
  { to: '/lineup', icon: '🎽', label: 'Lineup' },
  { to: '/attendance', icon: '✅', label: 'Attend' },
  { to: '/playbook', icon: '🏈', label: 'Plays' },
  { to: '/drills', icon: '📚', label: 'Drills' },
  { to: '/eval', icon: '⭐', label: 'Eval' },
  { to: '/settings', icon: '⚙️', label: 'Settings' },
];

export default function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthGate>
          <HashRouter>
            <LiveSessionProvider>
              <Shell />
            </LiveSessionProvider>
          </HashRouter>
        </AuthGate>
      </ConfirmProvider>
    </ToastProvider>
  );
}

function Shell() {
  const app = useApp();
  const loc = useLocation();
  const nav = NAV.filter((n) => !n.head || app.isHead);
  return (
    <div className="app">
      <nav className="rail">
        <h1>
          <AppMark size={30} />
          {APP.name}
        </h1>
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? 'active' : '')}>
            <span>{n.icon}</span>
            {n.label}
          </NavLink>
        ))}
        <NavLink to="/settings/team" className={({ isActive }) => (isActive ? 'active' : '')}>
          <span>👥</span>Team
        </NavLink>
        <div className="rail-foot">
          {app.team.name}
          <br />
          {app.userName} · {app.isHead ? 'head coach' : 'assistant'}
        </div>
      </nav>
      <div className="app-body">
        <header className="app-header">
          <h1>
            <AppMark size={26} />
            {app.team.name || APP.name} <LiveBadge />
          </h1>
          <SyncBadge />
          <NavLink to="/settings/team" className="btn btn-sm btn-ghost" title="Team">
            👥
          </NavLink>
        </header>
        <PolicyNotice />
        <main className={"app-main" + (loc.pathname.startsWith("/playbook") ? " app-main-wide" : "")}>
          <Suspense fallback={<div className="spinner" />}>
            <Routes>
              <Route path="/" element={<Navigate to="/plan" replace />} />
              <Route path="/plan" element={<PlanView />} />
              <Route path="/saved" element={<Navigate to="/plan" replace state={{ openSaved: true }} />} />
              <Route path="/drills" element={<DrillsView />} />
              <Route path="/drills/:tab" element={<DrillsView />} />
              <Route path="/playbook" element={<PlaybookView />} />
              <Route path="/attendance" element={<AttendanceView />} />
              <Route path="/lineup" element={<LineupView />} />
              <Route path="/eval" element={<EvalView />} />
              <Route path="/settings" element={<SettingsView />} />
              <Route path="/settings/:section" element={<SettingsView />} />
              <Route path="/team" element={<Navigate to="/settings/team" replace />} />
              <Route path="*" element={<Navigate to="/plan" replace />} />
            </Routes>
          </Suspense>
        </main>
        <nav className="tabbar">
          {nav.slice(0, 5).map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? 'active' : '')}>
              {n.icon}
              <span>{n.label}</span>
            </NavLink>
          ))}
          <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')}>
            ⋯<span>More</span>
          </NavLink>
        </nav>
      </div>
    </div>
  );
}
